import { c as _c } from "react/compiler-runtime";
import { type RechartsContentType as ContentType, RechartsTooltip } from '@precis-digital/kurama/recharts';
import { zIndexes } from '@precis-digital/kurama/theme';
import { useTheme } from '@precis-digital/kurama/utils';
import { type ReactNode } from 'react';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import { chartHasTooltip } from '../constants';
import type { ElementsDescriptionsMap, FirstOrThirdAxisDetails, SecondAxisDetails } from '../internalTypes';
import type { ChartCardTFunction, ChartDataRow, ChartSelections, ChartType, DrawingColorMaps } from '../types';
import DefaultTooltipContent from './DefaultTooltipContext';
interface ChartTooltipProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  chartType: ChartType;
  tooltipContentProp: ContentType<string | number, string> | undefined;
  firstAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  thirdAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow> | undefined;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  drawingColorMaps: DrawingColorMaps;
  selections: ChartSelections<DataKey>;
  t: ChartCardTFunction;
}
const ChartTooltip = t0 => {
  const $ = _c(16);
  const {
    chartType,
    tooltipContentProp,
    firstAxisDetails,
    secondAxisDetails,
    thirdAxisDetails,
    elementsDescriptionsMap,
    drawingColorMaps,
    selections,
    t
  } = t0;
  const theme = useTheme();
  const defaults = useChartCardDefaults();
  if (!chartHasTooltip[chartType]) {
    return null;
  }
  let t1;
  if ($[0] !== drawingColorMaps || $[1] !== elementsDescriptionsMap || $[2] !== firstAxisDetails || $[3] !== secondAxisDetails || $[4] !== selections || $[5] !== t || $[6] !== thirdAxisDetails || $[7] !== tooltipContentProp) {
    t1 = tooltipContentProp ?? <DefaultTooltipContent chartProps={{
      firstAxisDetails,
      secondAxisDetails,
      thirdAxisDetails,
      elementsDescriptionsMap,
      drawingColorMaps,
      selections,
      t
    }} />;
    $[0] = drawingColorMaps;
    $[1] = elementsDescriptionsMap;
    $[2] = firstAxisDetails;
    $[3] = secondAxisDetails;
    $[4] = selections;
    $[5] = t;
    $[6] = thirdAxisDetails;
    $[7] = tooltipContentProp;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const tooltipContent = t1;
  let t2;
  if ($[9] !== theme.tokens.background.tonal.soft.default) {
    t2 = {
      fill: theme.tokens.background.tonal.soft.default
    };
    $[9] = theme.tokens.background.tonal.soft.default;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      zIndex: zIndexes.chartTooltip,
      pointerEvents: "none"
    };
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== defaults.others.tooltipAnimationDuration || $[13] !== t2 || $[14] !== tooltipContent) {
    t4 = <RechartsTooltip content={tooltipContent} cursor={t2} animationDuration={defaults.others.tooltipAnimationDuration} wrapperStyle={t3} />;
    $[12] = defaults.others.tooltipAnimationDuration;
    $[13] = t2;
    $[14] = tooltipContent;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export default ChartTooltip;